import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import DashboardLayout from "../../Layout/DashboardLayout";
import {
  Icon,
  ConfirmDialog,
  SearchInput,
  hasPrivilege,
  employeeID,
  companyID, // import your app's companyId helper
} from "../../Common/UIComponents";
import EmailTemplateCreate from "./EmailTemplateCreate";
import EmailPreview from "../../Common/EmailPreview";
import {
  EmailConfig_GetEmailTemplateList,
  EmailConfig_DeleteEmailTemplate,
  EmailConfig_SaveEmailTemplateStatus,
} from "../../API_Service/EmailTemplate_Slice";
import { useNavigate, useLocation } from "react-router-dom";

// ─────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────

const groupByModule = (flatList) => {
  const map = new Map();
  for (const item of flatList) {
    const key = item.ModuleName;
    if (!map.has(key)) {
      map.set(key, {
        ModuleID: item.ModuleName,
        ModuleFunctionalityID: item.ModuleFunctionalityID,
        ModuleName: item.ModuleName ?? `Module ${key}`,
        children: [],
      });
    }
    map.get(key).children.push(item);
  }
  return Array.from(map.values());
};

// ─────────────────────────────────────────────
// DRAWER SHELL
// ─────────────────────────────────────────────

const DrawerShell = ({ isOpen, onClose, title, subtitle, icon, children }) => {
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [isOpen, onClose]);

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-[#181c1d]/20 backdrop-blur-sm z-[60]"
          aria-hidden="true"
          onClick={onClose}
        />
      )}
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`fixed top-0 right-0 h-full w-[620px] bg-white z-[70]
          shadow-[-20px_0px_40px_rgba(24,28,29,0.08)] flex flex-col
          transform transition-transform duration-300
          ${isOpen ? "translate-x-0" : "translate-x-full"}`}
      >
        <div className="px-8 py-6 flex items-center justify-between border-b border-[#bdc9c9]/20 shrink-0">
          <div className="flex items-start gap-3">
            {icon && (
              <span className="bg-[#005b60]/10 text-[#005b60] p-2 rounded-lg shrink-0">
                <Icon name={icon} className="text-[20px]" />
              </span>
            )}
            <div>
              <h2 className="text-xl font-bold text-[#181c1d]">{title}</h2>
              {subtitle && (
                <p className="text-sm text-[#3e494a] mt-0.5">{subtitle}</p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close panel"
            className="text-[#6e797a] hover:text-[#181c1d] transition-colors p-2
              hover:bg-[#e5e9e9] rounded-full ml-4 shrink-0
              focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#005b60]"
          >
            <Icon name="close" className="text-[22px]" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-8 py-6">{children}</div>
      </aside>
    </>
  );
};

// ─────────────────────────────────────────────
// TOGGLE SWITCH
// ─────────────────────────────────────────────

const ToggleSwitch = ({ checked, onChange, disabled }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={() => !disabled && onChange(!checked)}
    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors
      ${checked ? "bg-[#08757c]" : "bg-[#bdc9c9]"}
      ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
  >
    <span
      className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow
        transition-transform ${checked ? "translate-x-[18px]" : "translate-x-1"}`}
    />
  </button>
);

// ─────────────────────────────────────────────
// MODULE ROW (parent)
// ─────────────────────────────────────────────

const ModuleRow = ({ module, expanded, onExpand }) => (
  <tr className="border-b border-[#f0f3f3] hover:bg-[#f7fafa] transition-colors">
    <td className="px-6 py-3.5" colSpan={4}>
      <button
        type="button"
        onClick={onExpand}
        className="flex items-center gap-2 text-sm font-bold text-[#181c1d] hover:text-[#08757c] transition-colors"
      >
        <span
          className={`material-symbols-outlined text-[18px] text-[#3e494a] transition-transform duration-200
            ${expanded ? "rotate-90" : ""}`}
        >
          chevron_right
        </span>
        <span className="material-symbols-outlined text-[18px] text-[#08757c]">
          folder
        </span>
        {module.ModuleName}
        <span className="text-xs font-normal text-[#3e494a]/60 ml-1">
          ({module.children?.length ?? 0})
        </span>
      </button>
    </td>
  </tr>
);

// ─────────────────────────────────────────────
// FUNCTIONALITY ROW (child)
// ─────────────────────────────────────────────

const FunctionalityRow = ({
  item,
  canEdit,
  canDelete,
  onEdit,
  onDelete,
  onPreview,
  onStatusChange,
}) => (
  <tr className="border-b border-[#f0f3f3] bg-[#fafcfc] hover:bg-[#f3f8f8] transition-colors">
    <td className="pl-16 pr-4 py-3 text-sm text-[#181c1d]">
      {item.ModuleFunctionalityName}
    </td>
    <td className="px-4 py-3">
      <div className="flex items-center gap-1">
        {canEdit && (
          <buttont
            type="button"
            onClick={() => onEdit(item)}
            className="p-1.5 rounded-lg text-[#3e494a] hover:text-[#08757c] hover:bg-[#e5f7f6] transition-all"
            title="Edit"
          >
            <span className="material-symbols-outlined text-[18px]">edit</span>
          </buttont>
        )}
        {canDelete && (
          <button
            type="button"
            onClick={() => onDelete(item)}
            className="p-1.5 rounded-lg text-[#3e494a] hover:text-[#ba1a1a] hover:bg-red-50 transition-all"
            title="Delete"
          >
            <span className="material-symbols-outlined text-[18px]">
              delete
            </span>
          </button>
        )}
      </div>
    </td>
  </tr>
);

// ─────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────

export default function EmailTemplateIndex() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [modules, setModules] = useState([]);
  const [expandedIDs, setExpandedIDs] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  // Preview drawer
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewItem, setPreviewItem] = useState(null);

  // Delete dialog
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    item: null,
    loading: false,
    error: null,
  });

  const canEdit = true;
  const canCreate = true;
  const [editSuccess, setEditSuccess] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");

  const location = useLocation();

  // ── Load data ─────────────────────────────────
  const loadModules = useCallback(async () => {
    debugger;
    setIsLoading(true);
    try {
      const companyId = companyID;
      const res = await dispatch(
        EmailConfig_GetEmailTemplateList({ companyId }),
      );
      const payload = res?.payload;
      const flatList = Array.isArray(payload?.data?.Data)
        ? payload.data.Data
        : Array.isArray(payload?.Data)
          ? payload.Data
          : [];

      const grouped = groupByModule(flatList);
      setModules(grouped);
      console.log("API response for email templates:", res);
      console.log("Raw email templates data:", flatList);
      console.log("Loaded email templates:", grouped);
      if (grouped.length > 0) {
        setExpandedIDs(new Set([grouped[0].ModuleID]));
      }
    } catch (err) {
      console.error("Failed to load email templates:", err);
    } finally {
      setIsLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    loadModules();
  }, [loadModules]);

  useEffect(() => {
    if (location.state?.refresh) {
      loadModules();
      if (location.state?.saveSuccess) {
        setSuccessMsg(
          location.state?.isNew
            ? "Email template saved successfully."
            : "Email template updated successfully.",
        );
        setEditSuccess(true);
        setTimeout(() => setEditSuccess(false), 3000);
      }
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.refresh]);  

  // ── Search filter ────────────────────────────
  const filteredModules = modules
    .map((mod) => ({
      ...mod,
      children: (mod.children ?? []).filter(
        (c) =>
          !searchQuery ||
          c.FunctionalityName?.toLowerCase().includes(
            searchQuery.toLowerCase(),
          ) ||
          c.ToMails?.toLowerCase().includes(searchQuery.toLowerCase()),
      ),
    }))
    .filter(
      (mod) =>
        !searchQuery ||
        mod.ModuleName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        mod.children.length > 0,
    );

  // ── Expand / collapse ────────────────────────
  const toggleExpand = (moduleID) => {
    setExpandedIDs((prev) => {
      const next = new Set(prev);
      next.has(moduleID) ? next.delete(moduleID) : next.add(moduleID);
      return next;
    });
  };

  // ── Edit / Add ────────────────────────────────
  const handleEdit = useCallback(
    (item) => {
      console.log("EDIT item:", item); // verify fields
      navigate("/email-templateindex/edit", {
        state: {
          templateData: {
            ...item,
            // ModuleID is 0 from API — pass ModuleFunctionalityID so Create can use it
            ModuleFunctionalityID: item.ModuleFunctionalityID,
          },
        },
      });
    },
    [navigate],
  );

  const handleAddNew = useCallback(() => {
    navigate("/email-templateindex/create");
  }, [navigate]);

  // ── Delete ────────────────────────────────────
  const handleDeleteClick = useCallback((item) => {
    setDeleteDialog({ open: true, item, loading: false, error: null });
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    const { item } = deleteDialog;
    setDeleteDialog((prev) => ({ ...prev, loading: true }));
    try {
      // DELETE /EmailTemplate/DeleteEmailTemplate/{id}?modifiedBy={modifiedBy}
      const res = await dispatch(
        EmailConfig_DeleteEmailTemplate({
          id: item.EmailTemplateID,
          modifiedBy: employeeID,
        }),
      );
      const status =
        res?.payload?.data?.Status ??
        res?.payload?.data?.status ??
        res?.payload?.Status ??
        res?.payload?.status;
      if (status === "SUCCESS") {
        setDeleteDialog({
          open: false,
          item: null,
          loading: false,
          error: null,
        });
        setSuccessMsg("Email template deleted successfully.");
        setEditSuccess(true);
        setTimeout(() => setEditSuccess(false), 3000);
        loadModules();
      } else {
        throw new Error(
          res?.payload?.message ?? res?.payload?.Message ?? "Delete failed.",
        );
      }
    } catch (err) {
      setDeleteDialog((prev) => ({
        ...prev,
        loading: false,
        error: err?.message ?? "Failed to delete template.",
      }));
    }
  }, [deleteDialog, dispatch, loadModules]);

  // ── Status toggle ─────────────────────────────
  const handleStatusChange = useCallback(
    async (item, isActive) => {
      // Optimistic update
      setModules((prev) =>
        prev.map((mod) => ({
          ...mod,
          children: (mod.children ?? []).map((c) =>
            c.EmailTemplateID === item.EmailTemplateID
              ? { ...c, ISActive: isActive }
              : c,
          ),
        })),
      );

      try {
        // POST /EmailTemplate/SaveEmailTemplateStatus?emailTemplateId=&status=&companyId=
        await dispatch(
          EmailConfig_SaveEmailTemplateStatus({
            emailTemplateId: item.EmailTemplateID,
            status: isActive ? 1 : 0,
            companyID,
          }),
        );
      } catch {
        // Revert on failure
        setModules((prev) =>
          prev.map((mod) => ({
            ...mod,
            children: (mod.children ?? []).map((c) =>
              c.EmailTemplateID === item.EmailTemplateID
                ? { ...c, IsActive: !isActive }
                : c,
            ),
          })),
        );
      }
    },
    [dispatch],
  );

  // ── Preview ───────────────────────────────────
  const handlePreview = useCallback((item) => {
    setPreviewItem(item);
    setPreviewOpen(true);
  }, []);

  // ── Stats ─────────────────────────────────────
  const totalTemplates = modules.reduce(
    (acc, m) => acc + (m.children?.length ?? 0),
    0,
  );
  const activeTemplates = modules.reduce(
    (acc, m) => acc + (m.children ?? []).filter((c) => c.ISActive).length,
    0,
  );

  // ─────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────
  const content = (
    <div className="px-8 py-8 bg-[#f7fafa] min-h-screen text-[#181c1d]">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-xs text-[#3e494a] mb-2">
        <span>Settings</span>
        <span className="material-symbols-outlined text-[14px]">
          chevron_right
        </span>
        <span className="text-[#08757C] font-semibold">Email Templates</span>
      </nav>

      {/* Header */}
      <div className="flex flex-wrap justify-between items-end mb-8 gap-4">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-[#181c1d]">
            Email Templates
          </h1>
          <p className="text-[#3e494a] text-sm mt-1">
            Manage system email templates for all modules and functionalities.
          </p>
        </div>
        {canCreate && (
          <button
            onClick={handleAddNew}
            className="px-6 py-2.5 bg-gradient-to-br from-[#08757c] to-[#005b60] text-white
              text-sm font-bold rounded-xl shadow-lg shadow-[#08757c]/20
              hover:opacity-90 active:scale-95 transition-all flex items-center gap-2"
          >
            <span className="material-symbols-outlined text-[18px]">add</span>
            Add Template
          </button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        {[
          { label: "Total Modules", value: modules.length, icon: "folder" },
          { label: "Total Templates", value: totalTemplates, icon: "mail" },
          {
            label: "Active Templates",
            value: activeTemplates,
            icon: "mark_email_read",
          },
        ].map((s) => (
          <div
            key={s.label}
            className="bg-white rounded-2xl px-6 py-4 flex items-center gap-4
              shadow-[0px_8px_24px_rgba(24,28,29,0.06)] border border-white/40"
          >
            <div className="w-10 h-10 rounded-xl bg-[#e8f4f5] flex items-center justify-center flex-shrink-0">
              <span className="material-symbols-outlined text-[#08757c] text-[20px]">
                {s.icon}
              </span>
            </div>
            <div>
              <p className="text-2xl font-extrabold text-[#181c1d]">
                {s.value}
              </p>
              <p className="text-xs text-[#3e494a]/60 uppercase tracking-widest mt-0.5">
                {s.label}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Table card */}
      <div className="bg-white rounded-2xl shadow-[0px_20px_40px_rgba(24,28,29,0.06)] border border-white/40 overflow-hidden">
        {/* Toolbar */}
        <div className="px-6 py-4 border-b border-[#ebeeee] flex items-center justify-between gap-4">
          <SearchInput
            value={searchQuery}
            onChange={(v) => setSearchQuery(v)}
            placeholder="Search templates or modules…"
            className="w-72"
          />
          <div className="flex items-center gap-2">
            <button
              onClick={() =>
                setExpandedIDs(new Set(modules.map((m) => m.ModuleID)))
              }
              className="px-3 py-1.5 text-xs font-semibold text-[#3e494a] bg-[#e5e9e9]
                rounded-lg hover:bg-[#d8dcdc] transition-colors"
            >
              Expand All
            </button>
            <button
              onClick={() => setExpandedIDs(new Set())}
              className="px-3 py-1.5 text-xs font-semibold text-[#3e494a] bg-[#e5e9e9]
                rounded-lg hover:bg-[#d8dcdc] transition-colors"
            >
              Collapse All
            </button>
          </div>
        </div>

        {/* Table */}
        {isLoading ? (
          <div className="flex items-center justify-center h-48 text-[#3e494a] text-sm gap-3">
            <span className="material-symbols-outlined animate-spin text-[#08757c]">
              progress_activity
            </span>
            Loading templates…
          </div>
        ) : filteredModules.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 text-[#3e494a] text-sm gap-2">
            <span className="material-symbols-outlined text-[40px] text-[#bdc9c9]">
              mail_off
            </span>
            <p className="font-semibold">No templates found</p>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="text-[#08757c] text-xs hover:underline"
              >
                Clear search
              </button>
            )}
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-[#f7fafa]">
              <tr className="border-b border-[#ebeeee]">
                <th className="px-6 py-3 text-left text-xs font-bold text-[#3e494a] uppercase tracking-widest">
                  Functionality / Module
                </th>
                <th className="px-4 py-3 text-left text-xs font-bold text-[#3e494a] uppercase tracking-widest">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredModules.map((mod) => (
                <React.Fragment key={mod.ModuleID}>
                  <ModuleRow
                    module={mod}
                    expanded={expandedIDs.has(mod.ModuleID)}
                    onExpand={() => toggleExpand(mod.ModuleID)}
                  />
                  {expandedIDs.has(mod.ModuleID) &&
                    (mod.children ?? []).map((child) => (
                      <FunctionalityRow
                        key={child.EmailTemplateID}
                        item={child}
                        canEdit={canEdit}
                        canDelete={canEdit}
                        onEdit={handleEdit}
                        onDelete={handleDeleteClick}
                        onPreview={handlePreview}
                        onStatusChange={handleStatusChange}
                      />
                    ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Delete confirm */}
      <ConfirmDialog
        isOpen={deleteDialog.open}
        title="Delete Email Template"
        message={
          <p className="text-sm text-[#181c1d]">
            Are you sure you want to delete the template for{" "}
            <strong>{deleteDialog.item?.FunctionalityName}</strong>?
            <br />
            <span className="text-[#6e797a]">
              This action cannot be undone.
            </span>
          </p>
        }
        confirmLabel="Yes, Delete"
        isLoading={deleteDialog.loading}
        errorMessage={deleteDialog.error}
        onConfirm={handleDeleteConfirm}
        onCancel={() =>
          setDeleteDialog({
            open: false,
            item: null,
            loading: false,
            error: null,
          })
        }
      />
    </div>
  );

  return (
    <DashboardLayout>
      {content}
      {editSuccess && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[100] bg-[#bbf46e] text-[#112000] px-6 py-3 rounded-xl shadow-lg flex items-center gap-2 text-sm font-semibold"
          role="status"
        >
          <span className="material-symbols-outlined text-[20px]">
            check_circle
          </span>
          {successMsg}
        </div>
      )}
    </DashboardLayout>
  );
}
